import { Injectable } from '@angular/core';
import { Address } from '@src/app/core/models/address.model';
import { DropdownItem } from '@src/app/core/models/drop-down-list.model';
import { Product } from '@src/app/core/models/product.model';
import { SystemParameter } from '@src/app/core/models/system-parameter.model';
import { isValid, parse } from 'date-fns';

@Injectable()
export class UtilsService {
  parseToDate(value: string | null): Date | string | null {
    if (!value) {
      return value;
    }

    if (isValid(new Date(value))) {
      return new Date(value);
    }

    return value ? parse(value, 'yyyy-MM-dd', new Date()) : value;
  }

  /**
   * 取得搜尋條件產品下拉選單
   */
  getProductQueryDropdown(list: Product[]): SystemParameter[] {
    const options = list
      .reduce((pre, cur) => {
        if (cur.statusCode === 'ToBeActivated') {
          return pre;
        }

        const index = pre.findIndex(
          (item) => item.productCode === cur.productCode
        );

        if (index === -1) {
          return [...pre, cur];
        } else {
          if (pre[index].version < cur.version) {
            pre[index] = cur;
          }
          return pre;
        }
      }, [] as Product[])
      .map(
        (item) =>
          ({
            name: item.productName,
            code: item.productCode,
          } as SystemParameter)
      );

    options.sort((a, b) => a.code.localeCompare(b.code));

    return options;
  }

  mappingAddressByOptions(
    data: Address,
    options: {
      level1?: DropdownItem[];
      level2?: DropdownItem[];
      level3?: DropdownItem[];
      level4?: DropdownItem[];
    }
  ): Address {
    const { level1, level2, level3, level4, postcode } = data;
    return {
      level1: options.level1?.find(({ code }) => code === level1)?.name ?? '',
      level2: options.level2?.find(({ code }) => code === level2)?.name ?? '',
      level3: options.level3?.find(({ code }) => code === level3)?.name ?? '',
      level4: options.level4?.find(({ code }) => code === level4)?.name ?? '',
      postcode: postcode ? `00000${postcode}`.slice(-5) : '',
      content: data.content ?? '',
    };
  }

  /**
   * @description 將地址轉為顯示文字
   */
  convertAddressToString(data: Address): string {
    const { content, postcode, level1, level2, level3, level4 } =
      this.formatAddressText(data);

    return this.filterAndJoin([
      content,
      this.filterAndJoin(
        [postcode ?? '', this.filterAndJoin([level4, level3, level2, level1])],
        ' '
      ),
    ]);
  }

  /**
   * @description 整理地址文字（將 Other 轉成空字串）
   */
  private formatAddressText(data: Address): Address {
    return {
      content: data.content,
      level1: data.level1 === 'Other' ? '' : data.level1,
      level2: data.level2 === 'Other' ? '' : data.level2,
      level3: data.level3 === 'Other' ? '' : data.level3,
      level4: data.level4 === 'Other' ? '' : data.level4,
      postcode: data.postcode === 'Other' ? '' : data.postcode,
    };
  }

  private filterAndJoin(list: (string | null)[], separator = ', '): string {
    return list.filter((value) => !!value).join(separator);
  }

  /**
   * @description 檢查值是否在下拉選單中
   */
  checkValueIsInOptions<T = any>(
    value: T[keyof T],
    options: T[],
    field: keyof T
  ): boolean {
    return !!options.find((option) => option[field] === value);
  }
}
